import { defineStore } from "pinia";
import { ref, watch } from "vue";
import qz from "qz-tray";
import { useToast } from "vue-toastification";
import { KEYUTIL, KJUR } from "jsrsasign";
import { hextob64 } from "jsrsasign/lib/jsrsasign";
import jsPDF from "jspdf";
import { useOrderStore } from '@/stores/OrderStore';
import { bs } from "intl-tel-input/i18n";

const toast = useToast();

export const useQzStore = defineStore("qz", () => {
  const allPrinters = ref([]);
  const selectedPrinter = ref(null);
  const useQzTrayPrint = ref(false);
  const isQzConnected = ref(false);
  const isConnecting = ref(false);

  const orderStore = useOrderStore();

  const selectedSizeConfig = ref(null);
  const selectedTrayConfig = ref(null);
  const selectedMarginConfig = ref({ top: 0, right: 0, bottom: 0, left: 0 });
  const selectedScaleConfig = ref(0);

  const selectedBarcodePrinter = ref(null);
  const selectedBarcodeSizeConfig = ref(null);
  const selectedBarcodeTrayConfig = ref(null);
  const selectedBarcodeMarginConfig = ref({ top: 0, right: 0, bottom: 0, left: 0 });
  const selectedBarcodeScaleConfig = ref(0);
  const printWithoutPrice = ref(false);
  const isZebraPrinter = ref(false);
  const bsurItemName = 'BSUR Item';

  function setSelectedPrinterSizeWidthHeightandTray(size, tray) {
    selectedSizeConfig.value = size;
    selectedTrayConfig.value = tray;
  }

  function setSelectedBarcodePrinterSizeWidthHeightandTray(size, tray) {
    selectedBarcodeSizeConfig.value = size;
    selectedBarcodeTrayConfig.value = tray;
  }

  // Load saved printer from localStorage
  const initSelectedPrinter = () => {
    const saved = localStorage.getItem("selectedPrinter");
    if (saved) {
      selectedPrinter.value = { label: saved, value: saved };
    }
  };

  const initPrinterSettings = () => {
    const savedTray = localStorage.getItem("qz_selectedTrayConfig");
    const savedSize = localStorage.getItem("qz_selectedSizeConfig");
    const savedMargins = localStorage.getItem("qz_selectedMarginConfig");
    const savedScale = localStorage.getItem("qz_selectedScaleConfig");

    if (savedTray) {
      try {
        selectedTrayConfig.value = JSON.parse(savedTray);
      } catch (e) {
        console.warn("Failed to parse qz_selectedTrayConfig:", e);
        selectedTrayConfig.value = savedTray;
      }
    }

    if (savedSize) {
      try {
        selectedSizeConfig.value = JSON.parse(savedSize);
      } catch (e) {
        console.warn("Failed to parse qz_selectedSizeConfig:", e);
        selectedSizeConfig.value = savedSize;
      }
    }

    if (savedMargins) {
      try {
        selectedMarginConfig.value = JSON.parse(savedMargins);
      } catch (e) {
        console.warn("Failed to parse qz_selectedMarginConfig:", e);
        // keep existing default if parsing fails
      }
    }

    if (savedScale) {
      try {
        // stored as JSON.stringify so JSON.parse should yield a number or string
        const parsed = JSON.parse(savedScale);
        selectedScaleConfig.value = typeof parsed === "number" ? parsed : Number(parsed);
      } catch (e) {
        console.warn("Failed to parse qz_selectedScaleConfig:", e);
        selectedScaleConfig.value = Number(savedScale);
      }
    }
  }

  // Barcode printer settings initialization

  const initSelectedBarcodePrinter = () => {
    const saved = localStorage.getItem("qz_selectedBarcodePrinter");
    if (saved) {
      try {
        const parsedValue = JSON.parse(saved);
        selectedBarcodePrinter.value = { label: parsedValue, value: parsedValue };
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodePrinter:", e);
        // Fallback to using saved value directly if parsing fails
        selectedBarcodePrinter.value = { label: saved, value: saved };
      }
    }
  };

  const initBarcodePrinterSettings = () => {

    const savedTray = localStorage.getItem("qz_selectedBarcodeTrayConfig");
    const savedSize = localStorage.getItem("qz_selectedBarcodeSizeConfig");
    const savedMargins = localStorage.getItem("qz_selectedBarcodeMarginConfig");
    const savedScale = localStorage.getItem("qz_selectedBarcodeScaleConfig");


    if (savedTray) {
      try {
        selectedBarcodeTrayConfig.value = JSON.parse(savedTray);
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodeTrayConfig:", e);
        selectedBarcodeTrayConfig.value = savedTray;
      }
    }

    if (savedSize) {
      try {
        selectedBarcodeSizeConfig.value = JSON.parse(savedSize);
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodeSizeConfig:", e);
        selectedBarcodeSizeConfig.value = savedSize;
      }
    }

    if (savedMargins) {
      try {
        selectedBarcodeMarginConfig.value = JSON.parse(savedMargins);
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodeMarginConfig:", e);
        // keep existing default if parsing fails
      }
    }

    if (savedScale) {
      try {
        const parsed = JSON.parse(savedScale);
        selectedBarcodeScaleConfig.value = typeof parsed === "number" ? parsed : Number(parsed);
      } catch (e) {
        console.warn("Failed to parse qz_selectedBarcodeScaleConfig:", e);
        selectedBarcodeScaleConfig.value = Number(savedScale);
      }
    }
  };

  const initZebraPrinterSetting = () => {
    const saved = localStorage.getItem('qz_isZebraPrinter');
    isZebraPrinter.value = saved === 'true';
  };

  const initPrintWithoutPrice = () => {
    const saved = localStorage.getItem('qz_printWithoutPrice');
    if (saved !== null) {
      printWithoutPrice.value = saved === 'true';
    }
  };

  //Load saved printer mode from localStorage
  const initPrinterMode = () => {
    const saved = localStorage.getItem("useQzTrayPrint");
    if (saved !== null) {
      useQzTrayPrint.value = JSON.parse(saved);
    }
  };

  //Initialize QZ connection status
  const initQzConnectionStatus = () => {
    isQzConnected.value = qz.websocket.isActive();
  };

  // --- Persist selected printer ---
  watch(selectedPrinter, (newVal) => {
    if (newVal) {
      localStorage.setItem("selectedPrinter", JSON.stringify(newVal.value));
    }
  });

  //Persist printer mode
  watch(useQzTrayPrint, (newVal) => {
    localStorage.setItem("useQzTrayPrint", JSON.stringify(newVal));
  });

  // Watch selected tray, size, margin, and scale changes and save to localStorage
  watch(selectedTrayConfig, (newValue) => {
    localStorage.setItem("qz_selectedTrayConfig", JSON.stringify(newValue));
  }, { deep: true });

  watch(selectedSizeConfig, (newValue) => {
    localStorage.setItem("qz_selectedSizeConfig", JSON.stringify(newValue));
  }, { deep: true });

  watch(selectedMarginConfig, (newValue) => {
    localStorage.setItem("qz_selectedMarginConfig", JSON.stringify(newValue));
  }, { deep: true });

  watch(selectedScaleConfig, (newValue) => {
    localStorage.setItem("qz_selectedScaleConfig", JSON.stringify(newValue));
  });

  // Barcode printer settings watchers

  watch(selectedBarcodePrinter, (newVal) => {
    if (newVal) {
      localStorage.setItem("qz_selectedBarcodePrinter", JSON.stringify(newVal.value));
    }
  });

  watch(selectedBarcodeTrayConfig, (newValue) => {
    localStorage.setItem("qz_selectedBarcodeTrayConfig", JSON.stringify(newValue));
  }, { deep: true });

  watch(selectedBarcodeSizeConfig, (newValue) => {
    localStorage.setItem("qz_selectedBarcodeSizeConfig", JSON.stringify(newValue));
  }, { deep: true });

  watch(selectedBarcodeMarginConfig, (newValue) => {
    localStorage.setItem("qz_selectedBarcodeMarginConfig", JSON.stringify(newValue));
  }, { deep: true });

  watch(selectedBarcodeScaleConfig, (newValue) => {
    localStorage.setItem("qz_selectedBarcodeScaleConfig", JSON.stringify(newValue));
  });

  watch(isZebraPrinter, (newVal) => {
    localStorage.setItem('qz_isZebraPrinter', JSON.stringify(newVal));
  });

  watch(printWithoutPrice, (newVal) => {
    localStorage.setItem('qz_printWithoutPrice', JSON.stringify(newVal));
  });

  async function connectQZ(fetchPrinters = true) {
    // Prevent multiple simultaneous connection attempts
    if (isConnecting.value) {
      console.log("QZ connection already in progress, skipping...");
      return;
    }

    if (!qz.websocket.isActive()) {
      isConnecting.value = true;
      try {
        await qz.websocket.connect();
        console.log("Qz connected.");
        isQzConnected.value = true;
        // NOTE: do not reset useQzTrayPrint here. A successful connection must not
        // flip the user's "use QZ printing" intent back off — doing so reverted the
        // Settings toggle (via the panel's useQzTrayPrint watcher) and wrote
        // localStorage 'false', which then made the panel auto-disconnect on open.
        if (fetchPrinters) {
          await getAllPrinters();
        }
      } catch (e) {
        console.log("Failed to connect Qz.");
        isQzConnected.value = false;
        console.error(e);
      } finally {
        isConnecting.value = false;
      }
    } else {
      isQzConnected.value = true;
    }
  }

  async function disconnectQZ() {
    // Reset connecting flag when disconnecting
    isConnecting.value = false;

    if (qz.websocket.isActive()) {
      try {
        await qz.websocket.disconnect();
        console.log("Qz disconnected.");
        isQzConnected.value = false;
        useQzTrayPrint.value = false;
        allPrinters.value = [];
        selectedPrinter.value = null;
      } catch (e) {
        console.log("Failed to disconnect Qz.");
        console.error(e);
      }
    } else {
      isQzConnected.value = false;
    }
  }

  function initQZSecurity() {
    // Load the certificate
    qz.security.setCertificatePromise((resolve, reject) => {
      fetch("/certs/digital-certificate.txt")
        .then((res) => res.text())
        .then(resolve)
        .catch(reject);
    });

    // Sign messages with private key
    qz.security.setSignaturePromise((toSign) => {
      return (resolve, reject) => {
        fetch("/certs/private-key.txt")
          .then((res) => res.text())
          .then((pem) => {
            try {
              const key = KEYUTIL.getKey(pem);
              const sig = new KJUR.crypto.Signature({ alg: "SHA1withRSA" });
              sig.init(key);
              sig.updateString(toSign);
              const hex = sig.sign(); // hex signature
              const b64 = hextob64(hex); // convert → base64
              resolve(b64); // return base64 string
            } catch (err) {
              reject(err);
            }
          })
          .catch(reject);
      };
    });
  }

  const getAllPrinters = async () => {
    if (!qz.websocket.isActive()) {
      isQzConnected.value = false;
      toast.error(
        "QZ Tray is not connected. Please ensure QZ Tray is running and connected."
      );
      return;
    }

    try {
      const printers = await qz.printers.find();
      allPrinters.value = (Array.isArray(printers) ? printers : [printers])
        .filter(Boolean)
        .map((p) => ({ label: p, value: p })); // normalize format

      console.log("Available Printers:", allPrinters.value);

      if (allPrinters.value.length === 0) {
        toast.info("No printers found.");
        selectedPrinter.value = null;
        return;
      }

      toast.success(`Found ${allPrinters.value.length} printer(s).`);

      const savedPrinter = localStorage.getItem("selectedPrinter");
      if (savedPrinter) {
        // try to match saved printer
        const match = allPrinters.value.find(
          (p) => p.value === JSON.parse(savedPrinter)
        );
        if (match) {
          selectedPrinter.value = match;
        } else {
          // fallback to the first printer if saved printer not found
          selectedPrinter.value = allPrinters.value[0];
        }
      } else {
        // fallback to the first printer
        selectedPrinter.value = allPrinters.value[0];
      }

      // Restore barcode printer selection from localStorage
      const savedBarcodePrinter = localStorage.getItem("qz_selectedBarcodePrinter");
      if (savedBarcodePrinter) {
        // try to match saved barcode printer
        const match = allPrinters.value.find(
          (p) => p.value === JSON.parse(savedBarcodePrinter)
        );
        if (match) {
          selectedBarcodePrinter.value = match;
        }
      }
    } catch (error) {
      console.error("Error fetching printers:", error);
      toast.error("Failed to fetch printers. Please check QZ Tray connection.");
    }
  };

  const printBarcode = async (product) => {
    try {
      const barcode = product.Barcodes?.[0]?.Barcode || product.barcode;
      if (!barcode) {
        toast.error('No barcode available for this product');
        return;
      }

      const retailSku = product.RetailSku || product.sku;
      const productName = product.ItemName || product.Name || product.name || bsurItemName;
      const price = product.Branches?.find(b => b.PriceList === window.currentBranch?.PriceListName)?.Price ?? product.Price ?? product.price;
      const formattedPrice = price ? `$${parseFloat(price).toFixed(2)}` : '';

      if (isZebraPrinter.value) {
        // -------------------- Layout constants --------------------
        // Label: 2" × 1"  →  50.8 mm × 25.4 mm  →  406 × 203 dots at 203 DPI
        const LABEL_W  = 406;
        const LABEL_H  = 203;
        const LEFT_PAD = 10;
        const CONTENT_W = LABEL_W - LEFT_PAD * 2;  // 386

        const NAME_FONT  = 22;
        const SMALL_FONT = 18;
        const BAR_H      = 55;
        const NAME_BLOCK_H = NAME_FONT * 2;   // 2 lines × 22 = 44

        const sku          = retailSku    || '';
        const name         = productName || '';
        const barcodeValue = barcode     || '';
        const priceStr     = formattedPrice || '';

        const shouldPrintPrice = !!priceStr && (printWithoutPrice?.value === true);

        // Vertical centering: layout matches HTML order — SKU → Name → Barcode → Barcode# → Price
        // SKU: SMALL_FONT, gap 2, Name: NAME_BLOCK_H, gap 4, Bar: BAR_H, gap 2, Barcode#: SMALL_FONT, [gap 2, Price: SMALL_FONT]
        const TOTAL_H = SMALL_FONT + 2 + NAME_BLOCK_H + 4 + BAR_H + 2 + SMALL_FONT
                        + (shouldPrintPrice ? 2 + SMALL_FONT : 0);
        const Y0 = Math.floor((LABEL_H - TOTAL_H) / 2);

        const Y_SKU   = Y0;
        const Y_NAME  = Y_SKU  + SMALL_FONT + 2;
        const Y_BAR   = Y_NAME + NAME_BLOCK_H + 4;
        const Y_NUM   = Y_BAR  + BAR_H + 2;
        const Y_PRICE = Y_NUM  + SMALL_FONT + 2;

        const zplLines = ['^XA', `^PW${LABEL_W}`, `^LL${LABEL_H}`];

        // ─── Row 1: SKU — left-aligned ────────────────────────────────────────
        if (sku) {
          zplLines.push(
            `^FO${LEFT_PAD},${Y_SKU}^A0N,${SMALL_FONT},${SMALL_FONT}^FB${CONTENT_W},1,0,L^FD${sku}^FS`
          );
        }

        // ─── Row 2: Item name — 2 lines max, left-aligned, truncated after line 2 ──
        if (name) {
          zplLines.push(
            `^FO${LEFT_PAD},${Y_NAME}^A0N,${NAME_FONT},${NAME_FONT}^FB${CONTENT_W},2,0,L^FD${name}^FS`
          );
        }

        // ─── Row 3: Barcode — centered horizontally ───────────────────────────
        // Estimate Code 128 width: start(11) + N×data(11) + check(11) + stop(13) modules, module_width=2
        const barModules = 35 + 11 * barcodeValue.length;
        const estimatedBarcodeW = 2 * barModules;
        const barX = Math.max(0, Math.floor((LABEL_W - estimatedBarcodeW) / 2));
        zplLines.push(`^FO${barX},${Y_BAR}^BY2^BCN,${BAR_H},N,N^FD${barcodeValue}^FS`);

        // ─── Row 4: Barcode number centered ────────────────────────────────────
        zplLines.push(
          `^FO${LEFT_PAD},${Y_NUM}^A0N,${SMALL_FONT},${SMALL_FONT}^FB${CONTENT_W},1,0,C^FD${barcodeValue}^FS`
        );

        // ─── Row 5: Price centered (conditional) ───────────────────────────────
        if (shouldPrintPrice) {
          zplLines.push(
            `^FO${LEFT_PAD},${Y_PRICE}^A0N,${SMALL_FONT},${SMALL_FONT}^FB${CONTENT_W},1,0,C^FD${priceStr}^FS`
          );
        }

        zplLines.push('^XZ');

        if (!qz.websocket.isActive()) {
          isQzConnected.value = false;
          toast.error('QZ Tray is not connected. Please ensure QZ Tray is running.');
          return;
        }

        const printer = selectedBarcodePrinter.value || selectedPrinter.value;
        if (!printer) {
          toast.error('No printer selected. Please select a printer in settings.');
          return;
        }

        const config = qz.configs.create(printer.value);
        await qz.print(config, [{ type: 'raw', format: 'plain', data: zplLines.join('\n') }]);
        toast.success(`Barcode printed successfully on ${printer.label}`);

      } else {
        // ── HTML path: QZ Tray renders HTML for regular / non-ZPL printers ──
        const canvas = document.createElement('canvas');
        const JsBarcode = (await import('jsbarcode')).default;

        JsBarcode(canvas, barcode, {
          format: 'CODE128',
          width: 2,
          height: 60,
          displayValue: false,
          fontSize: 12,
          margin: 5,
        });

        const barcodeImage = canvas.toDataURL('image/png');

        const labelHtml = `
          <html>
            <head><title>Barcode Label</title></head>
            <body style="font-size: 14px;">
              <div style="width: 250px;">
                ${retailSku ? `${retailSku}` : ''}<br>
                <span style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; height: 35px;">
                  ${productName || ''}
                </span>
                <img src="${barcodeImage}" alt="Barcode" style="width: 100%; height: auto;" />
                <center>${barcode}</center>
                ${formattedPrice && printWithoutPrice.value ? `<center>${formattedPrice}</center>` : ''}
              </div>
            </body>
          </html>
        `;

        // Browser fallback when QZ is not connected
        if (!qz.websocket.isActive()) {
          isQzConnected.value = false;
          const printWindow = window.open('', '_blank');
          if (!printWindow) {
            toast.error('Popup blocked. Please allow popups to print the barcode.');
            return;
          }
          printWindow.document.open();
          printWindow.document.write(labelHtml);
          printWindow.document.close();
          printWindow.focus();
          printWindow.onload = function () {
            printWindow.print();
            setTimeout(() => printWindow.close(), 500);
          };
          return;
        }

        const printer = selectedBarcodePrinter.value || selectedPrinter.value;
        if (!printer) {
          toast.error('No printer selected. Please select a printer in settings.');
          return;
        }

        let configOptions = {
          copies: 1,
          rasterize: false,
          scaleContent: true,
          scale: selectedBarcodeScaleConfig.value || 0,
          margins: selectedBarcodeMarginConfig.value && Object.keys(selectedBarcodeMarginConfig.value).length > 0
            ? { ...selectedBarcodeMarginConfig.value }
            : { top: 0, right: 0, bottom: 0, left: 0 },
        };

        if (selectedBarcodeTrayConfig.value?.value) {
          configOptions.tray = selectedBarcodeTrayConfig.value.value;
        }

        if (selectedBarcodeSizeConfig.value?.label && barcodePrinterDetails.value) {
          const sizeName = selectedBarcodeSizeConfig.value.label;
          const printerSize = barcodePrinterDetails.value.sizes?.find(s => s.name === sizeName);
          if (printerSize?.in) {
            configOptions.size = { width: printerSize.in.width, height: printerSize.in.height, units: 'in' };
          } else if (printerSize?.mm) {
            configOptions.size = { width: printerSize.mm.width, height: printerSize.mm.height, units: 'mm' };
          } else {
            configOptions.size = { width: 50, height: 25, units: 'mm' };
          }
        } else {
          configOptions.size = { width: 50, height: 25, units: 'mm' };
        }

        const config = qz.configs.create(printer.value, configOptions);
        await qz.print(config, [{ type: 'pixel', format: 'html', flavor: 'plain', data: labelHtml }]);
        toast.success(`Barcode printed successfully on ${printer.label}`);
      }
    } catch (error) {
      console.error('Error printing barcode:', error);
      toast.error('Failed to print barcode. Please check printer connection.');
    }
  };

  const printInvoice = async () => {
    if (!qz.websocket.isActive()) {
      isQzConnected.value = false;
      toast.error("QZ Tray is not connected. Please ensure QZ Tray is running and connected.");
      return;
    }

    if (!selectedPrinter.value) {
      toast.error("No printer selected. Please select a printer in settings.");
      return;
    }

    try {
      const invoiceElement = document.querySelector(".invoice-print");
      if (!invoiceElement) {
        toast.error("Invoice content not found. Please ensure the invoice is visible.");
        return;
      }

      // Clone invoice and clean up
      const invoiceClone = invoiceElement.cloneNode(true);
      const printButton = invoiceClone.querySelector(".class-hide-on-print");
      if (printButton) printButton.remove();

      // Collect all styles
      let cssText = "";
      for (const sheet of document.styleSheets) {
        try {
          for (const rule of sheet.cssRules) {
            cssText += rule.cssText + "\n";
          }
        } catch (e) {
          console.warn("Skipping stylesheet due to CORS:", sheet.href);
        }
      }

      const printStyles = `
        @page {
          size: auto;
          margin: 0 !important;
        }

        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          margin: 0 !important;
          padding: 0 !important;
          width: 100%;
        }

        .invoice-print {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow: visible !important;
          margin: 0 !important;
          padding: 8px 16px !important;
          box-shadow: none !important;
          max-height: auto !important;
        }

        table {
          width: 100% !important;
          border-collapse: collapse !important;
          page-break-inside: auto !important;
        }

        tr, td, th {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          word-wrap: break-word !important;
          white-space: normal !important;
          font-size: 10px !important;
        }

        tr {
          page-break-after: auto !important;
        }

        .sidebar-menu, .checkout-Left, .class-hide-on-print {
          display: none !important;
        }
      `;
      cssText += printStyles;

      const invoiceHtml = `
        <html>
          <head>
            <meta charset="UTF-8">
            <style>${cssText}</style>
          </head>
          <body>
            ${invoiceClone.outerHTML}
          </body>
        </html>
      `;

      const printData = [
        {
          type: "pixel",
          format: "html",
          flavor: "plain",
          data: invoiceHtml,
        },
      ];

      // Use printer’s default settings but allow scaling
      // --- Prepare dynamic config ---
      let configOptions = {
        copies: 1,
        rasterize: false,
        scaleContent: true,
        scale: selectedScaleConfig.value || 0,
        margins: { ...selectedMarginConfig.value },
      };

      if (selectedTrayConfig.value && selectedTrayConfig.value.value) {
        configOptions.tray = selectedTrayConfig.value.value;
      }

      if (selectedSizeConfig.value && selectedSizeConfig.value.label) {
        const sizeName = selectedSizeConfig.value.label;
        const printerSize = printerDetails.value?.sizes?.find(s => s.name === sizeName);

        if (printerSize?.in) {
          configOptions.size = {
            width: printerSize.in.width,
            height: printerSize.in.height,
            units: "in",
          };
        } else if (printerSize?.mm) {
          configOptions.size = {
            width: printerSize.mm.width,
            height: printerSize.mm.height,
            units: "mm",
          };
        }
      }

      const config = qz.configs.create(selectedPrinter.value.value, configOptions);


      await qz.print(config, printData);
      toast.success(`Invoice printed successfully on ${selectedPrinter.value.label}`);
    } catch (error) {
      console.error("Error printing invoice:", error);
      toast.error("Failed to print invoice. Please check printer connection.");
    }
  };

  const printCashDrop = async (data) => {

    // Fallback logic if QZ Tray is not connected
    if (!qz.websocket.isActive()) {
      // Use window.print() logic similar to printInvoice fallback
      const { amount, date, closerName, posProfile } = data;

      const firstName = closerName ? closerName.split(' ')[0] : '';

      const printWindow = window.open("", "_blank");
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Cash Drop</title>
            <style>
              @page {
                size: auto;
                margin: 0 !important;
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0 !important;
                padding: 10px !important;
                width: 100%;
                font-size: 12px;
              }
              .cash-drop-container {
                width: 100%;
                max-width: 300px;
                margin: 0 auto;
              }
              .header {
                text-align: center;
                margin-bottom: 20px;
              }
              .header h2 {
                margin: 0;
                font-size: 18px;
                text-transform: uppercase;
              }
              .details {
                width: 100%;
                border-collapse: collapse;
              }
              .details td {
                padding: 5px 0;
              }
              .label {
                font-weight: bold;
                text-align: left;
              }
              .value {
                text-align: right;
              }
              .amount-row td {
                border-top: 1px dashed #000;
                border-bottom: 1px dashed #000;
                padding: 10px 0;
                font-size: 16px;
                font-weight: bold;
              }
              @media print {
                body, html {
                  height: auto !important;
                  overflow: visible !important;
                }
              }
            </style>
          </head>
          <body>
            <div class="cash-drop-container">
              <table class="details">
                <tr>
                  <td class="label">Date:</td>
                  <td class="value">${orderStore.formatDate(date)}</td>
                </tr>
                <tr>
                  <td class="label">POS Profile:</td>
                  <td class="value">${posProfile || ''}</td>
                </tr>
                <tr>
                  <td class="label">Closer:</td>
                  <td class="value">${firstName}</td>
                </tr>
                <tr class="amount-row">
                  <td class="label">Cash Drop:</td>
                  <td class="value">$${parseFloat(amount).toFixed(2)}</td>
                </tr>
              </table>
            </div>
            <script>
              window.onload = function() {
                window.print();
                setTimeout(() => window.close(), 500);
              };
            <\/script>
          </body>
        </html>
      `);
      printWindow.document.close();
      return;
    }

    if (!selectedPrinter.value) {
      toast.error("No printer selected. Please select a printer in settings.");
      return;
    }

    try {
      const { amount, date, closerName, posProfile } = data;

      // First name only
      const firstName = closerName ? closerName.split(' ')[0] : '';

      const printStyles = `
        @page {
          size: auto;
          margin: 0 !important;
        }

        * {
          box-sizing: border-box;
        }

        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          margin: 0 !important;
          padding: 0 !important;
          width: 100%;
        }

        .cash-drop-container {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          overflow: visible !important;
          margin: 0 !important;
          padding: 8px 16px !important;
          font-family: Arial, sans-serif;
          font-size: 12px;
        }

        .header {
          text-align: center;
          margin-bottom: 20px;
        }

        .header h2 {
          margin: 0;
          font-size: 18px;
          text-transform: uppercase;
        }

        .details {
          width: 100% !important;
          border-collapse: collapse !important;
        }

        .details td {
          padding: 5px 0;
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          word-wrap: break-word !important;
          white-space: normal !important;
        }

        .label {
          font-weight: bold;
          text-align: left;
        }

        .value {
          text-align: right;
        }

        .amount-row td {
          border-top: 1px dashed #000;
          border-bottom: 1px dashed #000;
          padding: 10px 0;
          font-size: 16px;
          font-weight: bold;
        }
      `;

      const htmlContent = `
        <html>
          <head>
            <meta charset="UTF-8">
            <style>${printStyles}</style>
          </head>
          <body>
            <div class="cash-drop-container">
              <div class="header">
              </div>
              <table class="details">
                <tr>
                  <td class="label">Date:</td>
                  <td class="value">${orderStore.formatDate(date)}</td>
                </tr>
                <tr>
                  <td class="label">POS Profile:</td>
                  <td class="value">${posProfile || ''}</td>
                </tr>
                <tr>
                  <td class="label">Closer:</td>
                  <td class="value">${firstName}</td>
                </tr>
                <tr class="amount-row">
                  <td class="label">Cash Drop:</td>
                  <td class="value">$${parseFloat(amount).toFixed(2)}</td>
                </tr>
              </table>
            </div>
          </body>
        </html>
      `;

      const printData = [
        {
          type: "pixel",
          format: "html",
          flavor: "plain",
          data: htmlContent,
        },
      ];

      // Configuration options
      let configOptions = {
        copies: 1,
        rasterize: false,
        scaleContent: true,
        scale: selectedScaleConfig.value || 0,
        margins: { ...selectedMarginConfig.value },
      };

      if (selectedTrayConfig.value && selectedTrayConfig.value.value) {
        configOptions.tray = selectedTrayConfig.value.value;
      }

      if (selectedSizeConfig.value && selectedSizeConfig.value.label) {
        const sizeName = selectedSizeConfig.value.label;
        const printerSize = printerDetails.value?.sizes?.find(s => s.name === sizeName);

        if (printerSize?.in) {
          configOptions.size = {
            width: printerSize.in.width,
            height: printerSize.in.height,
            units: "in",
          };
        } else if (printerSize?.mm) {
          configOptions.size = {
            width: printerSize.mm.width,
            height: printerSize.mm.height,
            units: "mm",
          };
        }
      }

      const config = qz.configs.create(selectedPrinter.value.value, configOptions);

      await qz.print(config, printData);
      toast.success(`Cash drop printed successfully on ${selectedPrinter.value.label}`);

    } catch (error) {
      console.error("Error printing cash drop:", error);
      toast.error("Failed to print cash drop.");
    }
  }

  const printerDetails = ref(null);

  const getPrinterDetails = async () => {
    if (!selectedPrinter.value) {
      toast.error("Please select a printer first.");
      return;
    }

    if (!qz.websocket.isActive()) {
      isQzConnected.value = false;
      toast.error(
        "QZ Tray is not connected. Please ensure QZ Tray is running and connected."
      );
      return;
    }

    try {
      // fetch full list of all printer details
      const details = await qz.printers.details();

      // find the matching printer
      const current = details.find(
        (p) => p.name === selectedPrinter.value.value
      );

      if (!current) {
        toast.error("Selected printer details not found.");
        return;
      }

      printerDetails.value = current;

      console.log("Selected Printer Details:", current);
      console.log("Available Paper Sizes:", current.sizes?.map(s => s.name));
      console.log("Available Trays:", current.trays);

      // toast.success(`Fetched details for ${current.name}`);
    } catch (error) {
      console.error("Error fetching printer details:", error);
      toast.error("Failed to fetch printer details.");
    }
  };

  const barcodePrinterDetails = ref(null);

  const getBarcodePrinterDetails = async () => {
    if (!selectedBarcodePrinter.value) {
      toast.error("Please select a barcode printer first.");
      return;
    }

    if (!qz.websocket.isActive()) {
      isQzConnected.value = false;
      toast.error(
        "QZ Tray is not connected. Please ensure QZ Tray is running and connected."
      );
      return;
    }

    try {
      // fetch full list of all printer details
      const details = await qz.printers.details();

      // find the matching printer
      const current = details.find(
        (p) => p.name === selectedBarcodePrinter.value.value
      );

      if (!current) {
        toast.error("Selected barcode printer details not found.");
        return;
      }

      barcodePrinterDetails.value = current;

      console.log("Selected Barcode Printer Details:", current);
      console.log("Available Paper Sizes:", current.sizes?.map(s => s.name));
      console.log("Available Trays:", current.trays);

      // toast.success(`Fetched details for ${current.name}`);
    } catch (error) {
      console.error("Error fetching barcode printer details:", error);
      toast.error("Failed to fetch barcode printer details.");
    }
  };

  return {
    allPrinters,
    selectedPrinter,
    useQzTrayPrint,
    isQzConnected,
    isConnecting,
    connectQZ,
    disconnectQZ,
    initQZSecurity,
    getAllPrinters,
    initSelectedPrinter,
    initPrinterMode,
    initQzConnectionStatus,
    printBarcode,
    printInvoice,
    getPrinterDetails,
    printerDetails,
    setSelectedPrinterSizeWidthHeightandTray,
    selectedSizeConfig,
    selectedTrayConfig,
    // initPrinterSizeAndTray,
    // initPrinterMarginAndScale,
    selectedMarginConfig,
    selectedScaleConfig,
    initPrinterSettings,

    // Barcode printer settings
    selectedBarcodePrinter,
    selectedBarcodeSizeConfig,
    selectedBarcodeTrayConfig,
    selectedBarcodeMarginConfig,
    selectedBarcodeScaleConfig,
    initBarcodePrinterSettings,
    initSelectedBarcodePrinter,
    setSelectedBarcodePrinterSizeWidthHeightandTray,
    getBarcodePrinterDetails,
    barcodePrinterDetails,
    printWithoutPrice,
    isZebraPrinter,
    initZebraPrinterSetting,
    initPrintWithoutPrice,
    printCashDrop,
  };
});
